import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import {
  HStack,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Text,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import Loader from '../../../../../components/Loader';
import { getContentRoute } from '../helpers';
import { LessonName } from '../index';
import { User } from '../../../../../context/AuthProvider';
import useTutorialInfo, {
  InfoStep,
} from '../../../../../hooks/useTutorialInfo';
import DotInfo from '../../../../../components/DotInfo';
import {
  ChalkUp,
  StudyGroup,
  TrackUnit,
} from '../../../../../generated/graphqlModels';
import { client } from '../../../../../index';
import { GET_CONTENT } from './Content';
import { EditIcon } from '@chakra-ui/icons';
import { colors } from '../../../../../App/ChakraTheme';

const areSetsEqual = (a: Set<any>, b: Set<any>) =>
  a.size === b.size && [...a].every((value) => b.has(value));

interface SidebarProps {
  studyGroup?: StudyGroup;
  isModuleLoading: boolean;
  trackUnit?: TrackUnit;
  chalkups?: ChalkUp[];
  context: User | null;
  isSaveAvailable: boolean;
  isSidebarOpened: boolean;
  handleOpenModal: () => void;
  module?: { id: string };
  content?: { id: string };
  isEdit?: boolean;
  isChallenge?: boolean;
}

const Sidebar: React.FC<SidebarProps> = (props) => {
  const {
    studyGroup,
    isModuleLoading,
    trackUnit,
    context,
    chalkups,
    isSaveAvailable,
    isSidebarOpened,
    handleOpenModal,
    module: selectedModule,
    content: selectedContent,
    isEdit,
    isChallenge,
  } = props;
  const [openedModuleIds, setOpenedModuleIds] = useState<string[]>([]);
  const location = useLocation();
  const programId = useParams<{ programId: string }>()?.programId;
  const { t } = useTranslation();
  const toggleModule = (moduleId: string) => {
    if (!openedModuleIds.includes(moduleId)) {
      setOpenedModuleIds([...openedModuleIds, moduleId]);
    } else {
      setOpenedModuleIds([...openedModuleIds.filter((id) => id !== moduleId)]);
    }
  };

  const { currentStep, nextStep } = useTutorialInfo();
  const isModuleInfoShown = currentStep === InfoStep.OPEN_MODULE;
  const isContentInfoShown = currentStep === InfoStep.OPEN_CONTENT;

  const [expandedIndexes, setExpandedIndexes] = useState<number[]>([]);

  useEffect(() => {
    if (!selectedModule) return;
    setOpenedModuleIds((prev) =>
      prev.includes(selectedModule.id) ? prev : [...prev, selectedModule.id],
    );
  }, [selectedModule]);
  useEffect(() => {
    if (trackUnit && trackUnit.trackModules) {
      const modules = trackUnit.trackModules.filter((m) => m.conditionShown);
      const activeIndexes: number[] = [];
      modules.forEach((m, index) => {
        if (openedModuleIds.includes(m.id)) {
          activeIndexes.push(index);
        }
      });
      const newIndexes = new Set(activeIndexes);
      const oldIndexes = new Set(expandedIndexes);
      if (!areSetsEqual(newIndexes, oldIndexes)) {
        setExpandedIndexes([...newIndexes]);
      }
    }
  }, [trackUnit, openedModuleIds]);

  const firstGuide =
    trackUnit &&
    trackUnit.trackModules &&
    trackUnit.trackModules[0]?.module.name === 'Teacher Guide'
      ? 1
      : 0;

  const navRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!navRef.current) return;
    if (isSidebarOpened) {
      navRef.current.removeAttribute('inert');
    } else {
      navRef.current.setAttribute('inert', '');
    }
  }, [isSidebarOpened]);

  return (
    <HStack
      ref={navRef}
      as="nav"
      aria-label="Lesson navigation"
      sx={{
        '.sidebar-opened &': {
          flex: '330px 0 0',
          width: '330px',
          opacity: 1,
        },
      }}
      transition="500ms"
      w={0}
      flex="0 0 0"
      overflowX="hidden"
      scrollBehavior="smooth"
      position="relative"
      opacity={0}
      className="sidebar"
      m="0"
      mt={5}
      zIndex={1}
    >
      {trackUnit && trackUnit.trackModules && (
        <Accordion
          index={expandedIndexes}
          // defaultIndex={expandedIndexes}
          height="auto"
          className="lfc"
          sx={{
            '.sidebar-opened &': {
              width: '310px',
              marginLeft: 0,
              opacity: 1,
            },
          }}
          transition="500ms"
          pl="8"
          pr="2"
          pt="12"
          pb="2"
          w="330px"
          ml="-330px"
          flexBasis="330px"
          flexShrink={0}
          flexGrow={0}
          overflowX="hidden"
          scrollBehavior="smooth"
          position="relative"
          opacity="0"
          allowMultiple
          allowToggle
        >
          {trackUnit?.trackModules
            ?.filter((m) => m.conditionShown)
            .map((trackModule, moduleIndex) => {
              const activeModule =
                selectedModule?.id === trackModule.id ||
                openedModuleIds.includes(trackModule.id);

              const hasDemoContent = trackModule.trackContents.find(
                (c) => c.content.isDemo,
              );

              const showModuleInfo =
                isModuleInfoShown &&
                trackUnit.trackModules.findIndex((m) =>
                  m.trackContents.find((c) => c.content.isDemo),
                ) === moduleIndex;

              return (
                <AccordionItem
                  padding={0}
                  margin={0}
                  border="none"
                  width="100%"
                  position="relative"
                  className={classNames('hlpAc1', {
                    disabled: !trackModule.conditionEnabled,
                  })}
                  id={`module-${trackModule.id}`}
                  key={trackModule.id}
                >
                  <AccordionButton
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleModule(trackModule.id);
                        if (showModuleInfo) nextStep();
                      }
                    }}
                    sx={{
                      '&:hover': {
                        background: 'transparent !important',
                        outline: 'none !important',
                      },
                      '&:focus': {
                        background: 'transparent !important',
                        outline: 'none !important',
                        boxShadow: 'none !important',
                      },
                      '&:focus-visible': {
                        outline: '2px solid #3182ce !important',
                        outlineOffset: '2px',
                        borderRadius: '4px',
                      },
                    }}
                    w="full"
                    padding={0}
                    margin={0}
                  >
                    <HStack
                      justify="start"
                      textAlign="start"
                      w="full"
                      data-number={moduleIndex + 1 - firstGuide}
                      className={classNames([
                        'module-title',
                        { 'active-module': activeModule },
                        {
                          done: trackModule.trackContents?.reduce(
                            (acc, trackContent) => {
                              if (!acc) {
                                return false;
                              }
                              const isDone =
                                chalkups?.find(
                                  (c) =>
                                    c.contentId === trackContent.content.id,
                                ) != null;
                              return isDone;
                            },
                            true,
                          ),
                        },
                      ])}
                      sx={{
                        '&.module-title:before': {
                          backgroundColor: '#fff',
                          marginRight: '16px',
                          content: 'attr(data-number)',
                          width: '42px',
                          height: '42px',
                          border: '1.5px solid rgba(213, 214, 222, 0.8)',
                          borderRadius: '50%',
                          textAlign: 'center',
                          fontWeight: 300,
                          fontSize: '24px',
                          color: 'rgb(213, 214, 222)',
                          transition: 'all 400ms',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          flexShrink: 0,
                          zIndex: 1,
                        },
                        '&.module-title.active-module:before': {
                          borderColor: 'rgb(18, 18, 59)',
                          color: 'rgb(18, 18, 59)',
                        },
                        '&:after': {
                          content: '""',
                          height: 'calc(100% - 44px)',
                          width: '1.5px',
                          position: 'absolute',
                          backgroundColor: 'rgba(213,214,222,0.4)',
                          left: '20.5px',
                          top: '42px',
                          zIndex: -1,
                        },
                        '&.done:before': {
                          color: '#fff',
                          background: colors.limegreen,
                          borderColor: colors.limegreen,
                        },
                        '&.done:after': {
                          backgroundColor: colors.limegreen,
                        },
                        '&.active-module': {
                          paddingBottom: '0px',
                        },
                        '.sidebar .lfc .hlpAc1:last-child &': {
                          paddingBottom: '0px',
                        },
                        '.sidebar .hlpAc1:last-of-type button &:after': {
                          height: 'calc(100% - 74px)',
                        },
                      }}
                      pb={4}
                      style={
                        context?.is_demo && !hasDemoContent
                          ? { opacity: 0.5 }
                          : {}
                      }
                      onClick={() => {
                        toggleModule(trackModule.id);
                        if (showModuleInfo) {
                          nextStep();
                        }
                      }}
                    >
                      <HStack
                        w="full"
                        h="full"
                        justify="space-between"
                        pb={0}
                        role="group"
                      >
                        <Text as="h5" textStyle="h5" whiteSpace="normal" wordBreak="break-word">
                          {trackModule.module.name}
                        </Text>
                        <HStack>
                          {isEdit && (
                            <EditIcon
                              display="none"
                              _groupHover={{ display: 'block' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                window.open(
                                  `/admin/syllabus/module/${trackModule.module.id}/change`,
                                );
                              }}
                            />
                          )}
                          <AccordionIcon
                            boxSize="6"
                            color="blue"
                            mr="0.7rem !important"
                            ml="0.5rem !important"
                          />
                        </HStack>
                      </HStack>
                      {showModuleInfo && (
                        <DotInfo
                          style={{ position: 'absolute', right: 0 }}
                          info="Open module"
                        />
                      )}
                    </HStack>
                  </AccordionButton>
                  <AccordionPanel
                    sx={{
                      '&:before': {
                        content: '""',
                        width: '1px',
                        position: 'absolute',
                        backgroundColor: 'rgba(213,214,222,0.4)',
                        left: '21px',
                        top: '0px',
                        height: '100%',
                        zIndex: -1,
                      },
                      '.sidebar .hlpAc1:last-of-type &:before': {
                        height: 'calc(100% - 30px)',
                      },
                    }}
                    margin={0}
                    pt={0}
                    pr="2px"
                    pl="14px"
                    pb="16px"
                    maxWidth="calc(100% - 30px)"
                    whiteSpace="pre-wrap"
                    wordBreak="break-word"
                    overflow="hidden"
                  >
                    {isModuleLoading && <Loader />}
                    {trackModule.trackContents?.map(
                      (trackContent, contentIndex) => {
                        const activeContent =
                          selectedContent?.id === trackContent.id;
                        const isDone = chalkups?.find(
                          (c) => c.contentId === trackContent.content.id,
                        );
                        if (!trackContent.content.isDemo && context?.is_demo) {
                          return (
                            <LessonName
                              key={trackContent.content.id}
                              disabled
                              isDone={!!isDone}
                            >
                              {trackContent.content.name}
                            </LessonName>
                          );
                        }
                        if (activeContent) {
                          return (
                            <LessonName
                              key={trackContent.id}
                              active={true}
                              isDone={!!isDone}
                            >
                              {trackContent.content.name}
                            </LessonName>
                          );
                        }
                        const showContentInfo =
                          isContentInfoShown &&
                          trackModule.trackContents.findIndex(
                            (c) => c.content.isDemo,
                          ) === contentIndex;
                        return (
                          <Link
                            key={trackContent.id}
                            onClick={(e) => {
                              if (showContentInfo) {
                                nextStep();
                              }
                              if (isSaveAvailable) {
                                alert('Please save all changes');
                                e.preventDefault();
                                e.stopPropagation();
                                return false;
                              }
                              return true;
                            }}
                            to={getContentRoute(
                              programId,
                              trackContent.content.id,
                              location.search,
                            )}
                            onMouseOver={() => {
                              client.query({
                                query: GET_CONTENT,
                                variables: {
                                  contentId: trackContent.content.id,
                                },
                              });
                            }}
                          >
                            <LessonName
                              active={false}
                              isDone={!!isDone}
                              highlight={
                                context?.is_demo && trackContent.content.isDemo
                              }
                            >
                              {trackContent.content.name}
                            </LessonName>
                            {showContentInfo && (
                              <DotInfo
                                style={{
                                  position: 'absolute',
                                  right: 0,
                                  top: 0,
                                }}
                                info="Open content"
                              />
                            )}
                          </Link>
                        );
                      },
                    )}
                    {isEdit && (
                      <Link
                        to={`/admin/syllabus/content/add/?module=${trackModule.module.id}`}
                        target="_blank"
                      >
                        {' '}
                        <LessonName
                          active={false}
                          isDone={false}
                          highlight={false}
                        >
                          {t('sidebar.addNewContent')}
                        </LessonName>
                      </Link>
                    )}
                  </AccordionPanel>
                </AccordionItem>
              );
            })}
          {isEdit && (
            <AccordionItem
              padding={0}
              margin={0}
              border="none"
              width="100%"
              position="relative"
            >
              <AccordionButton
                sx={{
                  '&:hover': {
                    background: 'transparent !important',
                    outline: 'none !important',
                  },
                  '&:focus': {
                    background: 'transparent !important',
                    outline: 'none !important',
                    boxShadow: 'none !important',
                  },
                }}
                w="full"
                padding={0}
                margin={0}
              >
                <HStack
                  justify="start"
                  textAlign="start"
                  w="full"
                  data-number={'+1'}
                  className={classNames(['module-title'])}
                  sx={{
                    '&.module-title:before': {
                      backgroundColor: '#fff',
                      marginRight: '16px',
                      content: 'attr(data-number)',
                      width: '42px',
                      height: '42px',
                      border: '1.5px solid rgba(213, 214, 222, 0.8)',
                      borderRadius: '50%',
                      textAlign: 'center',
                      fontWeight: 300,
                      fontSize: '24px',
                      color: 'rgb(213, 214, 222)',
                      transition: 'all 400ms',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      flexShrink: 0,
                      zIndex: 1,
                    },
                    '&.module-title.active-module:before': {
                      borderColor: 'rgb(18, 18, 59)',
                      color: 'rgb(18, 18, 59)',
                    },
                    '&:after': {
                      content: '""',
                      height: 'calc(100% - 44px)',
                      width: '1.5px',
                      position: 'absolute',
                      backgroundColor: 'rgba(213,214,222,0.4)',
                      left: '20.5px',
                      top: '42px',
                      zIndex: -1,
                    },
                    '&.done:before': {
                      color: '#fff',
                      background: colors.limegreen,
                      borderColor: colors.limegreen,
                    },
                    '&.done:after': {
                      backgroundColor: colors.limegreen,
                    },
                    '&.active-module': {
                      paddingBottom: '0px',
                    },
                    '.sidebar .lfc .hlpAc1:last-child &': {
                      paddingBottom: '0px',
                    },
                    '.sidebar .hlpAc1:last-of-type button &:after': {
                      height: 'calc(100% - 74px)',
                    },
                  }}
                  pb={4}
                  onClick={(e: any) => {
                    e.stopPropagation();
                    e.preventDefault();
                    window.open(
                      `/admin/syllabus/module/add/?unit=${trackUnit.unit.id}`,
                    );
                  }}
                >
                  <HStack w="full" h="full" justify="space-between" pb={0}>
                    <Text as="h5" textStyle="h5">
                      Add new module
                    </Text>
                  </HStack>
                </HStack>
              </AccordionButton>
            </AccordionItem>
          )}
        </Accordion>
      )}
    </HStack>
  );
};

export default Sidebar;
